<template>
  <div id="app">
    <!-- Top Navbar -->
    <nav>
      <NavMark />
      <ul class="nav-links">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li>
          <router-link to="/beats">Beats</router-link>
        </li>
        <li>
          <router-link to="/mix">Mix &amp; Master</router-link>
        </li>
        <li>
          <router-link to="/collab">Collab</router-link>
        </li>
        <li v-if="!user" class="nav-auth">
          <router-link to="/account">Sign In</router-link>
        </li>
        <li v-if="user" class="nav-notif">
          <router-link to="/profile" class="notif-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span v-if="unreadCount > 0" class="notif-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
          </router-link>
        </li>
        <li v-if="user" class="nav-user" @mouseenter="userMenuOpen = true" @mouseleave="userMenuOpen = false">
          <div class="nav-profile-link">
            <img v-if="user.picture" :src="user.picture" :alt="displayName" class="nav-avatar" />
            <span v-else class="nav-avatar nav-avatar-placeholder">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/><path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </span>
            {{ displayName }}
            <svg class="nav-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
          <transition name="ddrop">
            <ul v-if="userMenuOpen" class="user-dropdown">
              <li v-if="isAdmin">
                <router-link to="/admin" class="dd-item" @click.native="userMenuOpen = false">Admin View</router-link>
              </li>
              <template v-else-if="isEngineer">
                <li><router-link to="/engineer/jobs" class="dd-item" @click.native="userMenuOpen = false">Work</router-link></li>
                <li><router-link to="/engineer/beats" class="dd-item" @click.native="userMenuOpen = false">My Beats</router-link></li>
              </template>
              <li v-else-if="isArtist">
                <router-link to="/collab" class="dd-item" @click.native="userMenuOpen = false">Collabs</router-link>
              </li>
              <li><router-link to="/projects" class="dd-item" @click.native="userMenuOpen = false">My Projects</router-link></li>
              <li><router-link to="/beats/favourites" class="dd-item" @click.native="userMenuOpen = false">Favourites</router-link></li>
              <li><router-link to="/profile" class="dd-item" @click.native="userMenuOpen = false">Profile</router-link></li>
              <li><button class="dd-item dd-logout" @click="logout">Log Out</button></li>
            </ul>
          </transition>
        </li>
      </ul>
    </nav>

    <UploadResumeBanner />

    <!-- Main section -->
    <router-view v-slot="{ Component, route }">
      <transition :name="route.meta.transitionName">
        <component :is="Component" :key="route.path" />
      </transition>
    </router-view>

    <FootSection />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import NavMark from './components/NavMark.vue'
import FootSection from './components/FootSection.vue'
import UploadResumeBanner from './components/UploadResumeBanner.vue'

export default {
  components: { NavMark, FootSection, UploadResumeBanner },
  data: function () {
    return {
      userMenuOpen: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'USER',
      unreadCount: 'notifications/UNREAD_COUNT'
    }),
    displayName: function () {
      if (!this.user) return ''
      return this.user.alias || this.user.firstName || ''
    },
    isEngineer: function () { return this.user && this.user.role === 'ENGINEER' },
    isArtist: function () { return this.user && this.user.role === 'ARTIST' },
    isAdmin: function () { return this.user && this.user.role === 'ADMIN' }
  },
  watch: {
    $route: function () {
      if (this.user) this.$store.dispatch('notifications/FETCH')
    },
    user: function (newUser) {
      if (newUser) this.$store.dispatch('notifications/FETCH')
    }
  },
  methods: {
    logout: function () {
      this.$store.dispatch('LOG_OUT').then(() => {
        this.$router.push('/')
      })
    }
  }
}
</script>

<style lang="scss">
#app {
  min-height: 100vh;
  background: var(--black);
}

#app > nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 50px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 40px 6px;
  background: rgba(8,8,8,0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(201,168,76,0.12);
}

#app > nav > ul.nav-links {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 15px;
  list-style: none;
  margin: 0;
  padding: 0;

  a:not(.dd-item) {
    font-family: 'Montserrat', sans-serif;
    font-size: 10px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--gold-mid);
    text-decoration: none;
    transition: all 0.3s;
    font-weight: 300;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    padding: 4px 10px;
    border-radius: 3px;

    &:hover {
      color: var(--gold-light);
    }

    &.router-link-exact-active {
      color: var(--gold-bright);
      font-weight: 600;
      background: var(--purple-deep);
    }
  }
}

.nav-auth {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* User dropdown trigger */
.nav-user {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nav-profile-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: default;
  padding: 4px 10px;
  border-radius: 3px;
  font-family: 'Montserrat', sans-serif;
  font-size: 10px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: var(--gold-mid);
  font-weight: 300;
  white-space: nowrap;
  transition: color 0.25s;
}

.nav-user:hover .nav-profile-link {
  color: var(--gold-bright);
}

.nav-chevron {
  color: var(--gold-deep);
  opacity: 0.7;
  transition: transform 0.25s, opacity 0.25s;
  flex-shrink: 0;
}

.nav-user:hover .nav-chevron {
  transform: rotate(180deg);
  opacity: 1;
}

/* Dropdown menu */
.user-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: rgba(10, 6, 20, 0.97);
  border: 1px solid rgba(201,168,76,0.2);
  border-radius: 10px;
  padding: 6px;
  list-style: none;
  min-width: 150px;
  z-index: 200;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.06);
}

.dd-item {
  display: block;
  width: 100%;
  padding: 9px 14px;
  font-family: 'Montserrat', sans-serif;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--gold-mid);
  text-decoration: none;
  border-radius: 6px;
  transition: background 0.18s, color 0.18s;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(201,168,76,0.1);
    color: var(--gold-bright);
  }

  &.router-link-active {
    background: var(--purple-deep);
    color: var(--gold-bright);
  }
}

.dd-logout {
  color: rgba(231,76,60,0.7);
  margin-top: 2px;
  border-top: 1px solid rgba(255,255,255,0.05);
  padding-top: 11px;
  &:hover { background: rgba(231,76,60,0.08); color: #e74c3c; }
}

/* Dropdown enter/leave transition */
.ddrop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.ddrop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.ddrop-enter, .ddrop-leave-to { opacity: 0; transform: translateY(-6px); }

.nav-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(201,168,76,0.3);
  flex-shrink: 0;
}

.nav-avatar-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,168,76,0.08);
  color: var(--gold-mid);
}

.nav-notif {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.notif-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  color: var(--gold-mid);
  text-decoration: none;
  transition: color 0.25s;
}
.notif-btn:hover { color: var(--gold-bright); }
.notif-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #e74c3c;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 9px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

</style>
